import { lazy } from 'react'

import type { Route } from '@/types'

// auth routes
const SignIn = lazy(() => import('@pages/Auth/SignIn').then((module) => ({ default: module.SignIn })))
const SignUp = lazy(() => import('@pages/Auth/SignUp').then((module) => ({ default: module.SignUp })))
const ForgotPassword = lazy(() => import('@pages/Auth/ForgotPassword').then((module) => ({ default: module.ForgotPassword })))
const NewPassword = lazy(() => import('@pages/Auth/NewPassword').then((module) => ({ default: module.NewPassword })))

// dashboard routes
const Products = lazy(() => import('@pages/Catalog/Products').then((module) => ({ default: module.Products })))
const Categories = lazy(() => import('@pages/Catalog/Categories').then((module) => ({ default: module.Categories })))
const Characteristics = lazy(() => import('@pages/Catalog/Characteristics').then((module) => ({ default: module.Characteristics })))
const Manufacturers = lazy(() => import('@pages/Catalog/Manufacturers').then((module) => ({ default: module.Manufacturers })))
const SizeCharts = lazy(() => import('@pages/Catalog/SizeCharts').then((module) => ({ default: module.SizeCharts })))
const Reviews = lazy(() => import('@pages/Catalog/Reviews').then((module) => ({ default: module.Reviews })))
const Contents = lazy(() => import('@pages/Content/Contents').then((module) => ({ default: module.Contents })))
const News = lazy(() => import('@pages/Content/News').then((module) => ({ default: module.News })))
const StaticPages = lazy(() => import('@pages/Content/StaticPages').then((module) => ({ default: module.StaticPages })))
const Admins = lazy(() => import('@pages/Users/Admins').then((module) => ({ default: module.Admins })))
const UsersGroup = lazy(() => import('@pages/Users/UsersGroup').then((module) => ({ default: module.UsersGroup })))
const UsersDivisions = lazy(() => import('@pages/Users/UsersDivisions').then((module) => ({ default: module.UsersDivisions })))
const Leads = lazy(() => import('@pages/Shop/Leads').then((module) => ({ default: module.Leads })))
const Orders = lazy(() => import('@pages/Shop/Orders').then((module) => ({ default: module.Orders })))
const AllClients = lazy(() => import('@pages/Shop/Clients/AllClients').then((module) => ({ default: module.AllClients })))
const ClientGroups = lazy(() => import('@pages/Shop/Clients/ClientGroups').then((module) => ({ default: module.ClientGroups })))
const Reputation = lazy(() => import('@pages/Shop/Clients/Reputation').then((module) => ({ default: module.Reputation })))
const CustomFields = lazy(() => import('@pages/Shop/Clients/CustomFields').then((module) => ({ default: module.CustomFields })))
const Carts = lazy(() => import('@pages/Shop/Clients/Carts').then((module) => ({ default: module.Carts })))
const DeliveryAndPayments = lazy(() => import('@pages/Shop/DeliveryAndPayments').then((module) => ({ default: module.DeliveryAndPayments })))
const DeliveryServices = lazy(() => import('@pages/Shop/DeliveryAndPayments/DeliveryServices').then((module) => ({ default: module.DeliveryServices })))
const NPDetail = lazy(() => import('@pages/Shop/DeliveryAndPayments/Novaposhta').then((module) => ({ default: module.NPDetail })))
const UPDetail = lazy(() => import('@pages/Shop/DeliveryAndPayments/Ukrposhta').then((module) => ({ default: module.UPDetail })))
const Warehouses = lazy(() => import('@pages/Shop/Warehouses').then((module) => ({ default: module.Warehouses })))
const Coupons = lazy(() => import('@pages/Marketing/Coupons').then((module) => ({ default: module.Coupons })))
const Promotions = lazy(() => import('@pages/Marketing/Promotions').then((module) => ({ default: module.Promotions })))
const PriceGroups = lazy(() => import('@pages/Marketing/PriceGroups').then((module) => ({ default: module.PriceGroups })))
const Banners = lazy(() => import('@pages/Marketing/Banners').then((module) => ({ default: module.Banners })))
const Labels = lazy(() => import('@pages/Marketing/Labels').then((module) => ({ default: module.Labels })))
const MailingsEmail = lazy(() => import('@pages/Marketing/Mailings/MailingsEmail').then((module) => ({ default: module.MailingsEmail })))
const MailingsSegments = lazy(() => import('@pages/Marketing/Mailings/MailingsSegments').then((module) => ({ default: module.MailingsSegments })))
const MailingsSms = lazy(() => import('@pages/Marketing/Mailings/MailingsSms').then((module) => ({ default: module.MailingsSms })))
const LeadsReport = lazy(() => import('@pages/Reports/LeadsReport').then((module) => ({ default: module.LeadsReport })))
const SalesReport = lazy(() => import('@pages/Reports/SalesReport').then((module) => ({ default: module.SalesReport })))

// header routes
const Profile = lazy(() => import('@pages/Profile').then((module) => ({ default: module.Profile })))
const Settings = lazy(() => import('@pages/Settings').then((module) => ({ default: module.Settings })))
const ShopsSettings = lazy(() => import('@pages/Settings/ShopsSettings').then((module) => ({ default: module.ShopsSettings })))
const ShopSettings = lazy(() => import('@pages/Settings/ShopsSettings/components/ShopSettings').then((module) => ({ default: module.ShopSettings })))
const AdminPanelSettings = lazy(() => import('@pages/Settings/AdminPanelSettings').then((module) => ({ default: module.AdminPanelSettings })))
const LanguageDetail = lazy(() => import('@pages/Settings/AdminPanelSettings/components/LanguageDetail').then((module) => ({ default: module.LanguageDetail })))

export const routes: Route[] = [
  {
    rootPath: 'catalog',
    list: [
      {
        path: 'products',
        element: <Products />,
      },
      {
        path: 'categories',
        element: <Categories />,
      },
      {
        path: 'characteristics',
        element: <Characteristics />,
      },
      {
        path: 'manufacturers',
        element: <Manufacturers />,
      },
      {
        path: 'size-charts',
        element: <SizeCharts />,
      },
      {
        path: 'reviews',
        element: <Reviews />,
      },
    ],
  },
  {
    rootPath: 'content',
    list: [
      {
        path: 'contents',
        element: <Contents />,
      },
      {
        path: 'news',
        element: <News />,
      },
      {
        path: 'static-pages',
        element: <StaticPages />,
      },
    ],
  },
  {
    rootPath: 'shop',
    list: [
      {
        path: 'leads',
        element: <Leads />,
      },
      {
        path: 'orders',
        element: <Orders />,
      },
      {
        path: 'clients',
        list: [
          {
            path: 'all-clients',
            element: <AllClients />,
          },
          {
            path: 'client-groups',
            element: <ClientGroups />,
          },
          {
            path: 'reputation',
            element: <Reputation />,
          },
          {
            path: 'custom-fields',
            element: <CustomFields />,
          },
          {
            path: 'carts',
            element: <Carts />,
          },
        ],
      },
      {
        path: 'warehouses',
        element: <Warehouses />,
      },
      {
        path: 'delivery-and-payments',
        element: <DeliveryAndPayments />,
      },
      {
        path: 'delivery-services',
        element: <DeliveryServices />,
        hidden: true,
      },
      {
        path: 'delivery/novaposhta',
        element: <NPDetail />,
        hidden: true,
      },
      {
        path: 'delivery/ukrposhta',
        element: <UPDetail />,
        hidden: true,
      },
    ],
  },
  {
    rootPath: 'marketing',
    list: [
      {
        path: 'coupons',
        element: <Coupons />,
      },
      {
        path: 'promotions',
        element: <Promotions />,
      },
      {
        path: 'mailings',
        list: [
          {
            path: 'segments',
            element: <MailingsSegments />,
          },
          {
            path: 'email',
            element: <MailingsEmail />,
          },
          {
            path: 'sms',
            element: <MailingsSms />,
          },
        ],
      },
      {
        path: 'price-groups',
        element: <PriceGroups />,
      },
      {
        path: 'banners',
        element: <Banners />,
      },
      {
        path: 'labels',
        element: <Labels />,
      },
    ],
  },
  {
    rootPath: 'users',
    list: [
      {
        path: '',
        element: <Admins />,
      },
      {
        path: 'groups',
        element: <UsersGroup />,
      },
      {
        path: 'divisions',
        element: <UsersDivisions />,
      },
    ],
  },
  {
    rootPath: 'reports',
    list: [
      {
        path: 'leads',
        element: <LeadsReport />,
      },
      {
        path: 'sales',
        element: <SalesReport />,
      },
    ],
  },
]

export const authRoutes: Route[] = [
  {
    rootPath: 'auth',
    list: [
      {
        path: 'sign-in',
        element: <SignIn />,
      },
      {
        path: 'sign-up',
        element: <SignUp />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'new-password',
        element: <NewPassword />,
      },
    ],
  },
]

export const headerRoutes: Route[] = [
  {
    rootPath: 'settings',
    list: [
      {
        path: '',
        element: <Settings />,
      },
      {
        path: 'shops',
        element: <ShopsSettings />,
      },
      {
        path: 'shops/:id/edit',
        element: <ShopSettings />,
      },
      {
        path: 'shops/new',
        element: <ShopSettings />,
      },
      {
        path: 'admin-panel',
        element: <AdminPanelSettings />,
      },
      {
        path: 'admin-panel/:id/edit',
        element: <LanguageDetail />,
      },
      {
        path: 'admin-panel/new',
        element: <LanguageDetail />,
      },
    ],
  },
  {
    rootPath: '/',
    list: [
      {
        path: 'profile',
        element: <Profile />,
      },
    ],
  },
]
